import { Box, Skeleton, Typography } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useMemo, useState } from 'react';
import { collapsePills, PillLinkChip, PillRow } from 'src/components/Pill';
import { appendDeletedClientTextToName } from 'src/graphql/entities/client/client.utils';
import { appendDisabledUserTextToName } from 'src/graphql/entities/platform-user/utils';
import { SidePanelRowProps } from 'src/graphql/entities/side-panel-personalization';
import { useConversationEntity } from './conversation-entity-context';
import { ParticipantPill, ParticipantPillKind } from './participants/participant-pill';
import {
  ParticipantPillItem,
  useConversationParticipantPills,
} from './participants/use-conversation-participant-pills';
import { useParticipantPillActions } from './participants/use-participant-pill-actions';

const ENTITY_TO_KIND: Record<ConversationParticipantEntity, ParticipantPillKind> = {
  [ConversationParticipantEntity.PlatformUser]: 'agent',
  [ConversationParticipantEntity.Client]: 'client',
  [ConversationParticipantEntity.Rsm]: 'rsm',
  [ConversationParticipantEntity.Unknown]: 'unknown',
  [ConversationParticipantEntity.Bot]: 'agent',
};

const MIN_VISIBLE_PARTICIPANT_PILLS = 4;
const PILL_SKELETON_HEIGHT = '1.375rem';
const PILL_SKELETON_WIDTHS = ['5.75rem', '4.75rem', '4rem'];

const getPillLabel = (item: ParticipantPillItem): string => {
  if (item.isDeactivatedAgent) {
    return appendDisabledUserTextToName(item.displayName);
  }
  if (item.isDeletedClient) {
    return appendDeletedClientTextToName(item.displayName);
  }
  return item.displayName;
};

const PillSkeleton = ({ width }: { width: string }) => (
  <Skeleton
    variant="rounded"
    width={width}
    height={PILL_SKELETON_HEIGHT}
    sx={{ borderRadius: 999 }}
  />
);

export const ParticipantsRow = (_props: SidePanelRowProps) => {
  const { conversation, canEdit } = useConversationEntity();
  const { pills, isLoading, refetchParticipations } = useConversationParticipantPills({
    conversationId: conversation.conversationId,
    partnerId: conversation.partnerId,
  });
  const {
    getMenuItems,
    getMenuHelpText,
    getHoverCard,
    renderAddControl,
    pendingParticipants,
  } = useParticipantPillActions({ pills, refetchParticipations });

  const [expanded, setExpanded] = useState(false);

  const { visible, hiddenCount, isCollapsible } = useMemo(
    () =>
      collapsePills({
        items: pills,
        minVisible: MIN_VISIBLE_PARTICIPANT_PILLS,
        pinFirst: (item) => item.isUnidentified,
        expanded,
      }),
    [pills, expanded],
  );

  if (isLoading) {
    return (
      <PillRow label="Participants">
        {PILL_SKELETON_WIDTHS.map((width) => (
          <PillSkeleton key={width} width={width} />
        ))}
      </PillRow>
    );
  }

  return (
    <PillRow label="Participants">
      {visible.map((item) => {
        const kind = ENTITY_TO_KIND[item.entity] ?? 'unknown';

        const isCrossPartner =
          !!item.entityPartnerId && item.entityPartnerId !== conversation.partnerId;

        return (
          <ParticipantPill
            key={item.participantIdentity}
            kind={kind}
            label={getPillLabel(item)}
            title={isCrossPartner ? `Workspace: ${item.entityPartnerId}` : undefined}
            isFollower={item.isFollower}
            readOnly={!canEdit}
            menuItems={canEdit ? getMenuItems(item) : undefined}
            menuHelpText={canEdit ? getMenuHelpText(item) : undefined}
            hoverCard={getHoverCard(item)}
          />
        );
      })}

      {pendingParticipants.map((pending) => {
        const kind = ENTITY_TO_KIND[pending.entity] ?? 'unknown';

        return (
          <Box key={pending.key} sx={{ opacity: 0.55 }}>
            <ParticipantPill kind={kind} label={pending.displayName} readOnly />
          </Box>
        );
      })}

      {isCollapsible ? (
        <PillLinkChip type="button" onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Show less' : `+${hiddenCount} more`}
        </PillLinkChip>
      ) : null}

      {canEdit ? renderAddControl() : null}

      {!visible.length && !hiddenCount && !canEdit && (
        <Typography
          variant="body2"
          sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
        >
          No participants
        </Typography>
      )}
    </PillRow>
  );
};
